import { useEffect, useState, useMemo } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Eye, KeyRound, MailPlus, Shield, ShieldOff, X, RefreshCw, RotateCcw, FileText, Wallet, HeartPulse, Search, Edit2, Save, Trash2, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import KrakenLoader from "@/components/KrakenLoader";
import { useAuth } from "@/contexts/AuthContext";

const STATUS_BADGE = {
  // Forms
  Complete: "bg-green-500/15 text-green-300 border-green-400/40",
  "Partially Complete": "bg-yellow-500/15 text-yellow-300 border-yellow-400/40",
  Missing: "bg-red-500/15 text-red-300 border-red-400/40",
  Signed: "bg-green-500/15 text-green-300 border-green-400/40",
  "Not Signed": "bg-slate-500/15 text-slate-400 border-slate-400/30",
  // Payments
  Paid: "bg-green-500/15 text-green-300 border-green-400/40",
  Partial: "bg-yellow-500/15 text-yellow-300 border-yellow-400/40",
  Unpaid: "bg-slate-500/15 text-slate-400 border-slate-400/30",
  Overdue: "bg-red-500/15 text-red-300 border-red-400/40",
  // Account
  active: "bg-green-500/15 text-green-300 border-green-400/40",
  pending: "bg-yellow-500/15 text-yellow-300 border-yellow-400/40",
  disabled: "bg-red-500/15 text-red-300 border-red-400/40",
  // Role
  admin: "bg-purple-500/15 text-purple-300 border-purple-400/40",
  coach: "bg-blue-500/15 text-blue-300 border-blue-400/40",
  parent: "bg-slate-500/15 text-slate-300 border-slate-400/30",
  player: "bg-slate-500/15 text-slate-300 border-slate-400/30",
};

function Badge({ value, testId }) {
  const cls = STATUS_BADGE[value] || "bg-slate-500/15 text-slate-400 border-slate-400/30";
  return (
    <span data-testid={testId} className={`inline-block text-[10px] uppercase tracking-widest border rounded-full px-2.5 py-1 ${cls}`}>
      {value}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "Never";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function Accounts() {
  const { user: currentAdmin } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [viewingSig, setViewingSig] = useState(null);
  const [viewingMedical, setViewingMedical] = useState(null);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { account, typed, reason, reasonNote, deleting }

  const load = () => {
    setLoading(true);
    api.get("/admin/accounts").then((r) => setAccounts(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return accounts.filter((a) => {
      if (roleFilter !== "all" && a.role !== roleFilter) return false;
      if (statusFilter !== "all" && a.account_status !== statusFilter) return false;
      if (!n) return true;
      const hay = [a.name, a.player_name, a.email, a.phone, a.role].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(n);
    });
  }, [accounts, q, roleFilter, statusFilter]);

  const summary = useMemo(() => {
    const total = accounts.length;
    const formsComplete = accounts.filter((a) => a.forms_status.overall === "Complete").length;
    const formsMissing = accounts.filter((a) => a.forms_status.overall === "Missing").length;
    const paymentsPaid = accounts.filter((a) => a.payments_status.overall === "Paid").length;
    const paymentsOutstanding = total - paymentsPaid;
    return { total, formsComplete, formsMissing, paymentsPaid, paymentsOutstanding };
  }, [accounts]);

  const sendReset = async (acc) => {
    try {
      const { data } = await api.post(`/admin/accounts/${acc.id}/password-reset`);
      if (data.sent) toast.success(`Password reset sent to ${data.email}`);
      else toast.info(data.message || "Email reset is ready to connect.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const resendInvite = async (acc) => {
    try {
      const { data } = await api.post(`/admin/accounts/${acc.id}/invite`);
      if (data.sent) toast.success(`Invite sent to ${data.email}`);
      else toast.info(data.message || "Email invites are ready to connect.");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleStatus = async (acc) => {
    const next = acc.account_status === "active" ? "disabled" : "active";
    try {
      await api.post(`/admin/accounts/${acc.id}/status`, { account_status: next });
      toast.success(`Account ${next === "active" ? "activated" : "disabled"}`);
      load();
      if (detail && detail.id === acc.id) setDetail({ ...detail, account_status: next });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const changeStatus = async (acc, next) => {
    if (next === acc.account_status) return;
    try {
      await api.post(`/admin/accounts/${acc.id}/status`, { account_status: next });
      toast.success(`Status set to ${next}`);
      load();
      if (detail && detail.id === acc.id) setDetail({ ...detail, account_status: next });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (acc) => setEditing({
    id: acc.id, name: acc.name || "", player_name: acc.player_name || "",
    email: acc.email || "", phone: acc.phone || "",
    role: acc.role || "parent",
  });

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/admin/accounts/${editing.id}`, {
        name: editing.name, player_name: editing.player_name,
        email: editing.email, phone: editing.phone,
      });
      toast.success("Account updated");
      setAccounts((cur) => cur.map((a) => (a.id === data.id ? data : a)));
      if (detail && detail.id === data.id) setDetail(data);
      setEditing(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const viewSignedCopy = async (sigId) => {
    try {
      const { data } = await api.get(`/admin/signatures/${sigId}`);
      setViewingSig(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
  };

  const viewMedical = async (accountId, accountName) => {
    try {
      const { data } = await api.get(`/admin/medical-forms/${accountId}`);
      setViewingMedical({ ...data, _accountName: accountName });
    } catch (e) { toast.error(e.response?.data?.detail || "Medical form not submitted yet."); }
  };

  const resetSignature = async (sigId, label) => {
    if (!window.confirm(`Reset the ${label} signature so this family can sign again?`)) return;
    try {
      await api.delete(`/admin/signatures/${sigId}`);
      toast.success("Signature reset. The family can now re-sign.");
      // Refresh detail view from server
      if (detail) {
        const { data } = await api.get(`/admin/accounts/${detail.id}`);
        setDetail(data);
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const doDeleteAccount = async () => {
    const acc = confirmDelete?.account;
    if (!acc) return;
    const typedOk = (confirmDelete.typed || "").trim().toUpperCase() === "DELETE";
    if (!typedOk) {
      toast.error("Type DELETE to confirm");
      return;
    }
    if (currentAdmin && acc.id === currentAdmin.id) {
      toast.error("You cannot delete your own admin account while logged in.");
      return;
    }
    const reasonText = confirmDelete.reason === "Other"
      ? (confirmDelete.reasonNote || "Other")
      : (confirmDelete.reason || "");
    setConfirmDelete({ ...confirmDelete, deleting: true });
    try {
      await api.delete(`/admin/accounts/${acc.id}`, { data: { reason: reasonText } });
      toast.success("Account deleted successfully.");
      setConfirmDelete(null);
      setEditing(null);
      if (detail && detail.id === acc.id) setDetail(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to delete");
      setConfirmDelete((c) => c ? { ...c, deleting: false } : c);
    }
  };

  return (
    <div data-testid="accounts-page">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="eyebrow mb-1">Admin · Accounts</div>
          <h1 className="font-display text-3xl uppercase tracking-tight">Parent & Player Accounts</h1>
        </div>
        <button onClick={load} className="btn-ghost text-sm" data-testid="accounts-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4" data-testid="accounts-summary">
        {[
          ["Total", summary.total, "text-white"],
          ["Forms Complete", summary.formsComplete, "text-green-300"],
          ["Forms Missing", summary.formsMissing, "text-red-300"],
          ["Payments Paid", summary.paymentsPaid, "text-green-300"],
          ["Payments Outstanding", summary.paymentsOutstanding, "text-yellow-300"],
        ].map(([label, value, color]) => (
          <div key={label} className="kr-card rounded-xl p-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
            <div className={`font-display text-3xl ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, player, email, phone…"
            className="w-full pl-9 pr-3 py-2 bg-[#050A15] border border-white/10 rounded-md text-sm"
            data-testid="accounts-search"
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="accounts-filter-role">
          <option value="all">All roles</option>
          <option value="admin">Admin</option>
          <option value="coach">Coach</option>
          <option value="parent">Parent</option>
          <option value="player">Player</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#050A15] border border-white/10 rounded-md p-2 text-sm" data-testid="accounts-filter-status">
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="disabled">Disabled</option>
        </select>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 lg:hidden">
        {filtered.map((a) => (
          <div key={a.id} className="kr-card rounded-xl p-4" data-testid={`account-card-${a.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold truncate">{a.name || "—"}</div>
                <div className="text-xs text-slate-400 truncate">Player: {a.player_name || "—"}</div>
                <div className="text-xs text-slate-400 truncate">{a.email}</div>
                {a.phone && <div className="text-xs text-slate-400 truncate">{a.phone}</div>}
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Badge value={a.role || "parent"} />
                <Badge value={a.account_status} />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Badge value={a.forms_status.overall} />
              <Badge value={a.payments_status.overall} />
            </div>
            <div className="flex flex-wrap gap-2 mt-3 text-xs">
              <button onClick={() => setDetail(a)} className="btn-ghost text-xs" data-testid={`view-${a.id}-mobile`}><Eye size={12} /> View</button>
              <button onClick={() => openEdit(a)} className="btn-ghost text-xs" data-testid={`edit-${a.id}-mobile`}><Edit2 size={12} /> Edit</button>
              <Link to={`/admin/accounts/${a.id}/payments`} className="btn-ghost text-xs"><Wallet size={12} /> Payments</Link>
              <button onClick={() => sendReset(a)} className="btn-ghost text-xs"><KeyRound size={12} /> Reset</button>
              <button onClick={() => resendInvite(a)} className="btn-ghost text-xs"><MailPlus size={12} /> Invite</button>
              <select
                value={a.account_status}
                onChange={(e) => changeStatus(a, e.target.value)}
                className="bg-[#050A15] border border-white/10 rounded-md text-xs px-2 py-1"
                data-testid={`status-select-${a.id}-mobile`}
              >
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="disabled">Disabled</option>
              </select>
            </div>
          </div>
        ))}
        {!loading && filtered.length === 0 && (
          <div className="kr-card rounded-xl p-8 text-center text-slate-500 text-sm">
            {accounts.length === 0 ? "No accounts yet." : "No accounts match these filters."}
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden lg:block kr-card rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="accounts-table">
          <thead className="bg-[#14213D] uppercase text-[10px] tracking-widest text-slate-300">
            <tr>
              <th className="text-left p-3">Parent</th>
              <th className="text-left p-3">Player</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Phone</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Forms</th>
              <th className="text-left p-3">Payments</th>
              <th className="text-left p-3">Last Login</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((a) => (
              <tr key={a.id} className="border-t border-white/10 hover:bg-white/5" data-testid={`account-row-${a.id}`}>
                <td className="p-3 font-semibold">{a.name || "—"}</td>
                <td className="p-3 text-slate-300">{a.player_name || "—"}</td>
                <td className="p-3 text-slate-300 break-all">{a.email}</td>
                <td className="p-3 text-slate-300">{a.phone || "—"}</td>
                <td className="p-3"><Badge value={a.role || "parent"} testId={`role-${a.id}`} /></td>
                <td className="p-3">
                  <select
                    value={a.account_status}
                    onChange={(e) => changeStatus(a, e.target.value)}
                    className={`bg-[#050A15] border rounded-md text-[10px] uppercase tracking-widest px-2 py-1 cursor-pointer ${
                      a.account_status === "active" ? "border-green-400/40 text-green-300" :
                      a.account_status === "pending" ? "border-yellow-400/40 text-yellow-300" :
                      "border-red-400/40 text-red-300"
                    }`}
                    data-testid={`status-select-${a.id}`}
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="disabled">Disabled</option>
                  </select>
                </td>
                <td className="p-3"><Badge value={a.forms_status.overall} testId={`forms-${a.id}`} /></td>
                <td className="p-3"><Badge value={a.payments_status.overall} testId={`payments-${a.id}`} /></td>
                <td className="p-3 text-slate-400 text-xs whitespace-nowrap">{fmtDate(a.last_login_at)}</td>
                <td className="p-3">
                  <div className="flex gap-1.5 flex-wrap">
                    <button onClick={() => setDetail(a)} title="View details" className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]" data-testid={`view-${a.id}`}><Eye size={14} /></button>
                    <button onClick={() => openEdit(a)} title="Edit account" className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]" data-testid={`edit-${a.id}`}><Edit2 size={14} /></button>
                    <Link to={`/admin/accounts/${a.id}/payments`} title="Manage payments" className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]" data-testid={`manage-pay-${a.id}`}><Wallet size={14} /></Link>
                    <button onClick={() => sendReset(a)} title="Send password reset" className="p-1.5 hover:bg-white/10 rounded"><KeyRound size={14} /></button>
                    <button onClick={() => resendInvite(a)} title="Resend invite" className="p-1.5 hover:bg-white/10 rounded"><MailPlus size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div className="p-12 text-center text-slate-500">
            {accounts.length === 0 ? "No parent or player accounts yet." : "No accounts match these filters."}
          </div>
        )}
        {loading && <KrakenLoader label="Loading accounts…" testId="accounts-loader" />}
      </div>

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setDetail(null)} data-testid="account-detail-modal">
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8 relative">
            <button onClick={() => setDetail(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="account-detail-close"><X size={20} /></button>
            <div className="eyebrow mb-1">Account Detail</div>
            <h2 className="font-display text-2xl uppercase mb-4">{detail.name}</h2>

            <div className="grid sm:grid-cols-2 gap-4 mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Parent/Guardian</div>
                <div className="text-sm">{detail.name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Player</div>
                <div className="text-sm">{detail.player_name || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Email</div>
                <div className="text-sm break-all">{detail.email}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Phone</div>
                <div className="text-sm">{detail.phone || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Account Status</div>
                <div className="text-sm mt-1"><Badge value={detail.account_status} /></div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Last Login</div>
                <div className="text-sm">{fmtDate(detail.last_login_at)}</div>
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">Forms Checklist</div>
                <Badge value={detail.forms_status.overall} />
              </div>
              <div className="space-y-2">
                {detail.forms_status.items.map((f) => {
                  const isMedical = f.label === "Medical Form";
                  return (
                  <div key={f.label} className="flex items-center justify-between p-2.5 bg-[#050A15] rounded-md gap-2">
                    <span className="text-sm truncate flex-1 min-w-0">{f.label}</span>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {f.signed && (
                        <>
                          <button
                            onClick={() => isMedical ? viewMedical(detail.id, detail.name) : viewSignedCopy(f.signature_id)}
                            title={isMedical ? "View medical details" : "View signed copy"}
                            className="p-1.5 hover:bg-white/10 rounded text-[#00E5FF]"
                            data-testid={`view-sig-${f.signature_id}`}
                          >
                            {isMedical ? <HeartPulse size={14} /> : <FileText size={14} />}
                          </button>
                          <button
                            onClick={() => resetSignature(f.signature_id, f.label)}
                            title="Reset signature (allow re-sign)"
                            className="p-1.5 hover:bg-white/10 rounded text-yellow-400"
                            data-testid={`reset-sig-${f.signature_id}`}
                          >
                            <RotateCcw size={14} />
                          </button>
                        </>
                      )}
                      <Badge value={f.status} />
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>

            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="eyebrow">Payments Checklist</div>
                <Badge value={detail.payments_status.overall} />
              </div>
              <div className="space-y-2">
                {detail.payments_status.items.map((p) => (
                  <div key={p.label} className="flex items-center justify-between p-2.5 bg-[#050A15] rounded-md">
                    <span className="text-sm">{p.label}</span>
                    <div className="flex items-center gap-3">
                      {p.amount_due > 0 && <span className="text-xs text-slate-400">${p.amount_due.toFixed(2)} due</span>}
                      <Badge value={p.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-4 border-t border-white/10">
              <button onClick={() => { openEdit(detail); }} className="btn-ghost text-sm" data-testid="detail-edit"><Edit2 size={14} /> Edit Account</button>
              <button onClick={() => sendReset(detail)} className="btn-ghost text-sm"><KeyRound size={14} /> Send Password Reset</button>
              <button onClick={() => resendInvite(detail)} className="btn-ghost text-sm"><MailPlus size={14} /> Resend Invite</button>
              <button onClick={() => toggleStatus(detail)} className="btn-ghost text-sm">
                {detail.account_status === "active" ? <><ShieldOff size={14} /> Disable Account</> : <><Shield size={14} /> Activate Account</>}
              </button>
              <Link to={`/admin/accounts/${detail.id}/payments`} className="btn-ghost text-sm"><Wallet size={14} /> Manage Payments</Link>
            </div>
          </div>
        </div>
      )}

      {/* VIEW SIGNED COPY MODAL (admin) */}
      {viewingSig && (
        <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setViewingSig(null)} data-testid="admin-view-sig-modal">
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8 relative">
            <button onClick={() => setViewingSig(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={20} /></button>
            <div className="eyebrow mb-1">Signed Copy</div>
            <h2 className="font-display text-2xl uppercase mb-4">{viewingSig.document_title}</h2>
            {viewingSig.document_body && (
              <div className="bg-[#050A15] rounded-lg p-5 max-h-[40vh] overflow-y-auto border border-white/10 mb-5 text-slate-200 text-sm leading-relaxed whitespace-pre-wrap">
                {viewingSig.document_body.split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
                  /^\*\*[^*]+\*\*$/.test(p)
                    ? <strong key={i} className="text-white">{p.slice(2, -2)}</strong>
                    : <span key={i}>{p}</span>
                )}
              </div>
            )}
            <div className="border-t border-white/10 pt-4 space-y-2 text-sm">
              {viewingSig.co_signed_name && (
                <div className="flex justify-between"><span className="text-slate-400">Player typed name:</span><span className="text-white font-semibold">{viewingSig.signed_name}</span></div>
              )}
              <div className="flex justify-between">
                <span className="text-slate-400">{viewingSig.co_signed_name ? "Parent typed name:" : "Signed by:"}</span>
                <span className="text-white font-semibold">{viewingSig.co_signed_name || viewingSig.signed_name}</span>
              </div>
              <div className="flex justify-between"><span className="text-slate-400">Date signed:</span><span className="text-white">{fmtDate(viewingSig.signed_at)}</span></div>
              {viewingSig.photo_video_consent && (
                <div className="flex justify-between" data-testid="admin-view-photo-video-consent">
                  <span className="text-slate-400">Photo/Video consent:</span>
                  <span className={viewingSig.photo_video_consent === "yes" ? "text-[#00E5FF] font-semibold" : "text-red-300 font-semibold"}>
                    {viewingSig.photo_video_consent === "yes" ? "Yes — Permission granted" : "No — Do not use"}
                  </span>
                </div>
              )}
              <div className="flex justify-between"><span className="text-slate-400">Agreement:</span><span className="text-[#00E5FF]">Accepted</span></div>
            </div>
          </div>
        </div>
      )}

      {/* VIEW MEDICAL DETAILS MODAL (admin) */}
      {viewingMedical && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto"
          onClick={() => setViewingMedical(null)}
          data-testid="admin-view-medical-modal"
        >
          <div onClick={(e) => e.stopPropagation()} className="kr-card rounded-2xl p-6 sm:p-8 max-w-2xl w-full my-8 relative">
            <button onClick={() => setViewingMedical(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white"><X size={20} /></button>
            <div className="eyebrow mb-1">Medical Information</div>
            <h2 className="font-display text-2xl uppercase mb-1">{viewingMedical.player_name || viewingMedical._accountName}</h2>
            <p className="text-xs text-slate-400 mb-5">
              Submitted {fmtDate(viewingMedical.created_at)}
              {viewingMedical.updated_at && viewingMedical.updated_at !== viewingMedical.created_at && (
                <> · Last updated {fmtDate(viewingMedical.updated_at)}</>
              )}
            </p>

            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
              {[
                ["Player Name", viewingMedical.player_name],
                ["Date of Birth", viewingMedical.dob],
                ["Season", viewingMedical.season],
                ["Parent / Guardian", viewingMedical.parent_name],
                ["Phone", viewingMedical.phone],
                ["Email", viewingMedical.email],
                ["Emergency Contact", viewingMedical.emergency_contact_name],
                ["Relationship", viewingMedical.emergency_relationship],
                ["Emergency Phone", viewingMedical.emergency_phone],
                ["First Contact In Emergency",
                  viewingMedical.emergency_first_contact === "other"
                    ? `Other — ${viewingMedical.emergency_first_contact_other}`
                    : viewingMedical.emergency_first_contact === "emergency"
                      ? "Emergency contact" : "Parent listed above"],
              ].map(([label, value]) => (
                <div key={label}>
                  <div className="text-[10px] uppercase tracking-widest text-[#00E5FF]">{label}</div>
                  <div className="text-white break-words">{value || "—"}</div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3">
              <div className="eyebrow">Medical History</div>
              {[
                ["Allergies", viewingMedical.allergies, viewingMedical.allergies_detail],
                ["Medications", viewingMedical.medications, viewingMedical.medications_detail],
                ["Medical Conditions", viewingMedical.conditions, viewingMedical.conditions_detail],
                ["Recent Injuries / Surgeries", viewingMedical.injuries, viewingMedical.injuries_detail],
                ["Activity Restrictions", viewingMedical.restrictions, viewingMedical.restrictions_detail],
              ].map(([label, flag, detailStr]) => (
                <div key={label} className="bg-[#050A15] border border-white/10 rounded-md p-3">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold">{label}</span>
                    <span className={`text-[10px] uppercase tracking-widest border rounded-full px-2 py-0.5 ${
                      flag ? "bg-yellow-500/15 text-yellow-300 border-yellow-400/40" : "bg-slate-500/15 text-slate-400 border-slate-400/30"
                    }`}>{flag ? "Yes" : "No"}</span>
                  </div>
                  {flag && detailStr && <div className="text-sm text-slate-200 mt-2 whitespace-pre-wrap">{detailStr}</div>}
                </div>
              ))}

              {viewingMedical.other_notes && (
                <div className="bg-[#050A15] border border-white/10 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-[#00E5FF] mb-1">Other Notes</div>
                  <div className="text-sm text-slate-200 whitespace-pre-wrap">{viewingMedical.other_notes}</div>
                </div>
              )}
            </div>

            <div className="border-t border-white/10 pt-4 mt-5 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-400">Emergency care permission:</span><span className={viewingMedical.emergency_care_permission ? "text-[#00E5FF]" : "text-red-300"}>{viewingMedical.emergency_care_permission ? "Granted" : "Not granted"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Accuracy certification:</span><span className={viewingMedical.accuracy_certification ? "text-[#00E5FF]" : "text-red-300"}>{viewingMedical.accuracy_certification ? "Confirmed" : "Not confirmed"}</span></div>
              <div className="flex justify-between"><span className="text-slate-400">Parent typed signature:</span><span className="text-white font-semibold">{viewingMedical.signed_name || "—"}</span></div>
            </div>

            <div className="flex justify-end pt-4 border-t border-white/10 mt-4">
              <button onClick={() => window.print()} className="btn-ghost text-sm">Print</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT ACCOUNT MODAL */}
      {editing && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm overflow-y-auto"
          onClick={() => setEditing(null)}
          data-testid="edit-account-modal"
        >
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <form
              onClick={(e) => e.stopPropagation()}
              onSubmit={saveEdit}
              className="kr-card rounded-2xl p-6 sm:p-8 max-w-md w-full my-8 relative space-y-3"
            >
              <button type="button" onClick={() => setEditing(null)} className="absolute top-4 right-4 text-slate-400 hover:text-white">
                <X size={20} />
              </button>
              <div className="eyebrow">Edit Account</div>
              <h2 className="font-display text-2xl uppercase mb-4">Update Info</h2>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Parent / Guardian Name</span>
                <input
                  required
                  value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2.5 text-sm"
                  data-testid="edit-name"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Player Name</span>
                <input
                  value={editing.player_name}
                  onChange={(e) => setEditing({ ...editing, player_name: e.target.value })}
                  className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2.5 text-sm"
                  data-testid="edit-player-name"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Email</span>
                <input
                  required
                  type="email"
                  value={editing.email}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2.5 text-sm"
                  data-testid="edit-email"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Phone</span>
                <input
                  value={editing.phone}
                  onChange={(e) => setEditing({ ...editing, phone: e.target.value })}
                  className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2.5 text-sm"
                  data-testid="edit-phone"
                />
              </label>
              <div className="flex gap-2 pt-3">
                <button type="button" onClick={() => setEditing(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
                <button type="submit" className="btn-primary flex-1 justify-center" data-testid="edit-save"><Save size={14} /> Save</button>
              </div>

              {/* DANGER ZONE — Delete Account */}
              <div className="mt-6 pt-5 border-t border-red-400/20" data-testid="danger-zone">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle size={14} className="text-red-300" />
                  <div className="text-[10px] uppercase tracking-widest text-red-300">Danger Zone</div>
                </div>
                <p className="text-xs text-slate-400 mb-3">
                  Deleting an account archives it and all of its payments, signed documents, and medical info. The parent will no longer be able to log in. This cannot be undone.
                </p>
                <button
                  type="button"
                  onClick={() => setConfirmDelete({
                    account: editing, typed: "", reason: "", reasonNote: "",
                  })}
                  className="w-full inline-flex items-center justify-center gap-2 bg-red-500/15 border border-red-400/40 text-red-200 hover:bg-red-500/25 rounded-md px-3 py-2 text-sm"
                  data-testid="open-delete-account"
                >
                  <Trash2 size={14} /> Delete Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DELETE ACCOUNT CONFIRMATION MODAL */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm overflow-y-auto"
          onClick={() => setConfirmDelete(null)}
          data-testid="delete-account-modal"
        >
          <div className="min-h-full flex items-start sm:items-center justify-center p-4">
            <div
              onClick={(e) => e.stopPropagation()}
              className="kr-card rounded-2xl p-6 sm:p-8 max-w-md w-full my-8 relative border border-red-400/30"
            >
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="absolute top-4 right-4 text-slate-400 hover:text-white"
                aria-label="Close"
              >
                <X size={20} />
              </button>
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="text-red-300" size={18} />
                <div className="eyebrow text-red-300">Confirm account deletion</div>
              </div>
              <h2 className="font-display text-2xl uppercase">Delete {confirmDelete.account.name || "Account"}?</h2>
              <p className="text-sm text-slate-300 mt-3">
                Are you sure you want to delete this account? This action cannot be undone.
              </p>
              <div className="mt-3 bg-[#050A15]/60 border border-white/10 rounded-md p-3 text-xs text-slate-400 space-y-1">
                <div><span className="text-slate-500">Email:</span> <span className="text-slate-200">{confirmDelete.account.email}</span></div>
                {confirmDelete.account.player_name && (
                  <div><span className="text-slate-500">Player:</span> <span className="text-slate-200">{confirmDelete.account.player_name}</span></div>
                )}
                <div><span className="text-slate-500">Role:</span> <span className="text-slate-200">{confirmDelete.account.role || "parent"}</span></div>
              </div>

              <label className="block mt-4">
                <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Reason (optional)</span>
                <select
                  value={confirmDelete.reason}
                  onChange={(e) => setConfirmDelete({ ...confirmDelete, reason: e.target.value })}
                  className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm"
                  data-testid="delete-reason"
                >
                  <option value="">— Select a reason —</option>
                  <option>Duplicate account</option>
                  <option>Inactive account</option>
                  <option>Wrong email</option>
                  <option>Test account</option>
                  <option>Other</option>
                </select>
              </label>
              {confirmDelete.reason === "Other" && (
                <label className="block mt-2">
                  <span className="text-[10px] uppercase tracking-widest text-[#00E5FF]">Reason note</span>
                  <input
                    value={confirmDelete.reasonNote}
                    onChange={(e) => setConfirmDelete({ ...confirmDelete, reasonNote: e.target.value })}
                    placeholder="Brief note for the audit log"
                    className="mt-1 w-full bg-[#050A15] border border-white/10 rounded-md p-2 text-sm"
                    data-testid="delete-reason-note"
                  />
                </label>
              )}

              <label className="block mt-4">
                <span className="text-[10px] uppercase tracking-widest text-red-300">Type DELETE to confirm</span>
                <input
                  autoFocus
                  value={confirmDelete.typed}
                  onChange={(e) => setConfirmDelete({ ...confirmDelete, typed: e.target.value })}
                  placeholder="DELETE"
                  className="mt-1 w-full bg-[#050A15] border border-red-400/40 rounded-md p-2 text-sm tracking-widest uppercase"
                  data-testid="delete-typed-input"
                />
              </label>

              <div className="flex gap-2 pt-5">
                <button
                  type="button"
                  onClick={() => setConfirmDelete(null)}
                  disabled={!!confirmDelete.deleting}
                  className="btn-ghost flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="delete-account-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={doDeleteAccount}
                  disabled={(confirmDelete.typed || "").trim().toUpperCase() !== "DELETE" || !!confirmDelete.deleting}
                  className="flex-1 justify-center inline-flex items-center gap-1 bg-red-500/25 border border-red-400/60 text-red-100 hover:bg-red-500/40 disabled:opacity-40 disabled:cursor-not-allowed rounded-md px-3 py-2 text-sm"
                  data-testid="delete-account-confirm"
                >
                  <Trash2 size={14} /> {confirmDelete.deleting ? "Deleting…" : "Delete account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
